import { ProForm, ProFormCaptcha, ProFormField, ProFormText } from '@ant-design/pro-form';
import { Button, Checkbox, Form, message, Typography } from 'antd';
import classNames from 'classnames';
import { has, isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { getParamFromUrl } from '@/infrastructure/history';
import { useAuthContext } from '@/presentation/hooks';
import { LoginType } from '@/presentation/pages/login/types';
import { authService } from '@/service/auth';
import { RegisterType, tencentCloudService } from '@/service/tencent-cloud';
import { isIOTPhone, isPhone } from '@raft/utils/src/validator';
import styles from './index.module.scss';

export interface RegisterFormProps {
  onSubmit?: () => void;
}

const { Link } = Typography;

export type RegisterFormValues = {
  phone: string;
  verifyCode: string;
  agree: boolean;
};

export const RegisterForm = (props: RegisterFormProps) => {
  const {} = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { onLoginSuccess } = useAuthContext();
  const [timing, setTiming] = useState(false);
  const [submitEnable, setSubmitEnable] = useState(false);

  // 升级antd之后再用
  // const phone = Form.useWatch('phone', form);
  // const verifyCode = Form.useWatch('verifyCode', form);
  // const agree = Form.useWatch('agree', form);
  // const canSubmit = useMemo(() => (phone && verifyCode && agree), [phone, verifyCode, agree]);

  const onFinish = async (values: RegisterFormValues) => {
    if (!values.agree) {
      message.warn('请先同意隐私保护协议和Bugly平台协议');
      return;
    }

    setLoading(true);
    const registerToken = await tencentCloudService.saasLogin({
      phoneNumber: values.phone,
      phoneVerifyCode: values.verifyCode,
      registerType: getParamFromUrl('registerType') as RegisterType,
      token: getParamFromUrl('token'),
    });

    if (registerToken) {
      const res = await authService.login(LoginType.TencentCloud, {
        saasToken: registerToken.key,
        uin: registerToken.uin,
        ownerUin: registerToken.uin,
        // 手机号base64
        p: btoa(values.phone),
      });

      if (res) {
        onLoginSuccess(LoginType.TencentCloud, res?.userInfo, {
          ...registerToken,
          buglyToken: res?.buglyToken,
          saasToken: registerToken.key,
        });
      } else {
        message.error('登录失败，请稍后再试');
      }
    }

    setLoading(false);
  };

  const sendCode = async () => {
    const { phone } = form.getFieldsValue();
    const res = await authService.sendSmsVerifyCode(phone);

    if (!res) {
      message.error('验证码发送失败，请稍后再试！');
      throw new Error('send fail');
    }
  };

  return (
    <ProForm<RegisterFormValues>
      form={form}
      style={{ width: 400 }}
      className={styles.registerForm}
      onFinish={onFinish}
      onValuesChange={(values) => {
        if (has(values, 'agree')) {
          setSubmitEnable(values.agree);
        }
      }}
      submitter={{
        render: () => <Button
            type={'primary'}
            htmlType={'submit'}
            disabled={!submitEnable}
            block
            size={'large'}
            loading={loading}
          >立即注册</Button>,
      }}
    >
      <ProFormText
        name={'phone'}
        label={''}
        placeholder={'安全手机'}
        help="请填写安全手机号，用于找回帐号、找回密码、帐号保护等场景"
        rules={[
          {
            required: true,
            message: '请填写安全手机号，用于找回帐号、找回密码、帐号保护等场景',
          },
          {
            validator: (_rule, value, callback) => {
              if (isIOTPhone(value)) {
                callback('不能绑定物联网码号');
              }
              if (!isPhone(value)) {
                callback('手机号码格式不正确');
              }

              callback();
            },
          },
        ]}
        fieldProps={{
          addonBefore: '+86',
          className: styles.phoneInput,
        }}
      />
      <ProForm.Item shouldUpdate>
        {(form) => {
          const { phone } = form.getFieldsValue();
          const hasError = form.getFieldError('verifyCode');

          return (
            <ProFormCaptcha
              phoneName={'phone'}
              name={'verifyCode'}
              help={'请输入短信中的6位数验证码'}
              placeholder={'验证码'}
              rules={[
                { required: true, message: '请填写验证码' },
              ]}
              fieldProps={{
                className: styles.captchaInput,
              }}
              captchaProps={{
                className: classNames({
                  [styles.captchaBtn]: true,
                  [styles.captchaBtnWithError]: !isEmpty(hasError),
                  [styles.captchaBtnWithTiming]: timing,
                }),
                disabled: isIOTPhone(phone) || !isPhone(phone) || timing,
                loading: false,
              }}
              countDown={60}
              onGetCaptcha={sendCode}
              captchaTextRender={(timing: boolean, count: number) => {
                setTiming(timing);
                if (!timing) {
                  return '发送验证码';
                }
                return `${count}秒后可重发`;
              }}
            />
          );
        }}
      </ProForm.Item>
      <ProFormField name={'agree'} label={''} valuePropName="checked">
        <Checkbox>
          我已阅读并同意
          <Link href={'https://privacy.qq.com/document/preview/fc748b3d96224fdb825ea79e132c1a56'} target={'_blank'}>
            隐私保护协议</Link>
          和
          <Link>Bugly平台协议</Link>
        </Checkbox>
      </ProFormField>
    </ProForm>
  );
};
